import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";

import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowNumber } from "../../../common/controls";
import { DocumentProcessingStatus } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { ChunkingStrategy } from "../../../../api/graphql";
import { FormControl } from "@mui/material";

export type TenantDocumentViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  nameValue?: { displayId: string; id: string } | null;
  name: string;
  descriptionValue?: { displayId: string; id: string } | null;
  description: string;
  fileFiles: CrudRows;
  fileViewAction?: IMenuAction[];
  fileTypeValue?: { displayId: string; id: string } | null;
  fileType: string;
  fileSizeValue?: { displayId: string; id: string } | null;
  fileSize: number;
  statusOptions: { id: string; displayId: string }[];
  status: DocumentProcessingStatus;
  chunkCountValue?: { displayId: string; id: string } | null;
  chunkCount: number | undefined;
  uploadedAtValue?: { displayId: string; id: string } | null;
  uploadedAt: Date | null;
  processedAtValue?: { displayId: string; id: string } | null;
  processedAt: Date | null;
  processingErrorValue?: { displayId: string; id: string } | null;
  processingError: string;
  contentTypeValue?: { displayId: string; id: string } | null;
  contentType: string;
  chunkingStrategyOptions: { id: string; displayId: string }[];
  chunkingStrategy: ChunkingStrategy;
  chunkingSeparatorRegexValue?: { displayId: string; id: string } | null;
  chunkingSeparatorRegex: string;
  chunkingMaxTokensValue?: { displayId: string; id: string } | null;
  chunkingMaxTokens: number | undefined;
  chunkingOverlapTokensValue?: { displayId: string; id: string } | null;
  chunkingOverlapTokens: number | undefined;
};

export const TenantDocumentViewForm: React.FC<TenantDocumentViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      name = "",
      description = "",
      fileFiles,
      fileViewAction,
      fileType = "",
      fileSize = 0,
      statusOptions = [],
      status = "" as DocumentProcessingStatus,
      chunkCount = 0,
      uploadedAt = null,
      processedAt = null,
      processingError = "",
      contentType = "",
      chunkingStrategyOptions = [],
      chunkingStrategy = "" as ChunkingStrategy,
      chunkingSeparatorRegex = "",
      chunkingMaxTokens = 0,
      chunkingOverlapTokens = 0,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <GroupContainer
              id="null"
              title={langStore.groupTenantDocumentLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <ShowMultiLine
                  value={name}
                  label={langStore.TenantDocumentNameLabel}
                />
                <ShowMultiLine
                  value={description}
                  label={langStore.TenantDocumentDescriptionLabel}
                />
                <ShowFilesCompact
                  rows={fileFiles}
                  showLabel={true}
                  label={langStore.TenantDocumentFileLabel}
                  menuActions={fileViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <ShowMultiLine
                  value={fileType}
                  label={langStore.TenantDocumentFileTypeLabel}
                />
                <ShowNumber
                  value={fileSize}
                  label={langStore.TenantDocumentFileSizeLabel}
                />
                <ShowString
                  value={
                    status
                      ? statusOptions.find(
                          (o: { id: string }) => o.id === status
                        )?.displayId || status
                      : ""
                  }
                  label={langStore.TenantDocumentStatusLabel}
                />
                <ShowNumber
                  value={chunkCount}
                  label={langStore.TenantDocumentChunkCountLabel}
                />
                <ShowDateTime
                  value={uploadedAt}
                  label={langStore.TenantDocumentUploadedAtLabel}
                />
                <ShowDateTime
                  value={processedAt}
                  label={langStore.TenantDocumentProcessedAtLabel}
                />
                <ShowMultiLine
                  value={processingError}
                  label={langStore.TenantDocumentProcessingErrorLabel}
                />
                <ShowMultiLine
                  value={contentType}
                  label={langStore.TenantDocumentContentTypeLabel}
                />
                <ShowString
                  value={
                    chunkingStrategy
                      ? chunkingStrategyOptions.find(
                          (o: { id: string }) => o.id === chunkingStrategy
                        )?.displayId || chunkingStrategy
                      : ""
                  }
                  label={langStore.TenantDocumentChunkingStrategyLabel}
                />
                <ShowMultiLine
                  value={chunkingSeparatorRegex}
                  label={langStore.TenantDocumentChunkingSeparatorRegexLabel}
                />
                <ShowNumber
                  value={chunkingMaxTokens}
                  label={langStore.TenantDocumentChunkingMaxTokensLabel}
                />
                <ShowNumber
                  value={chunkingOverlapTokens}
                  label={langStore.TenantDocumentChunkingOverlapTokensLabel}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
